import React, { useEffect, useState } from "react";
import { RefreshCw, X } from "lucide-react";

/**
 * AppUpdateBanner — detects when a new service worker version has installed
 * and offers the user a one-tap reload. Standard PWA pattern.
 *
 * Pairs with /public/service-worker.js (which uses skipWaiting + clients.claim,
 * so the new SW activates automatically — we just need to refresh the HTML/JS
 * the user is currently viewing).
 */
const AppUpdateBanner = () => {
  const [updateReady, setUpdateReady] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return undefined;

    let cancelled = false;

    const handleNewWorker = (worker) => {
      const onStateChange = () => {
        // Show the banner only when a NEW worker is installed AND there is an existing controller
        // (i.e., not the first-ever install — only on updates)
        if (worker.state === "installed" && navigator.serviceWorker.controller && !cancelled) {
          setUpdateReady(true);
        }
      };
      worker.addEventListener("statechange", onStateChange);
    };

    navigator.serviceWorker.ready.then((reg) => {
      if (cancelled) return;

      // If a new worker is already waiting/installing when we arrive
      if (reg.waiting && navigator.serviceWorker.controller) {
        setUpdateReady(true);
      }
      if (reg.installing) handleNewWorker(reg.installing);

      // Listen for future updates
      reg.addEventListener("updatefound", () => {
        if (reg.installing) handleNewWorker(reg.installing);
      });
    });

    // When the active controller changes, the page is now served by the new SW —
    // reload once so we get the fresh HTML/JS that matches.
    let refreshing = false;
    const onControllerChange = () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);

    return () => {
      cancelled = true;
      navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
    };
  }, []);

  if (!updateReady || dismissed) return null;

  const handleReload = () => {
    // Most updates already activated via skipWaiting; reloading picks up fresh HTML/JS.
    window.location.reload();
  };

  return (
    <div
      data-testid="app-update-banner"
      className="fixed top-0 inset-x-0 z-[9999] bg-gradient-to-r from-amber-600 to-amber-500 text-white shadow-lg"
      role="status"
    >
      <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-center gap-3">
        <RefreshCw className="w-4 h-4 flex-shrink-0 animate-spin-slow" />
        <p className="text-sm sm:text-[0.95rem] flex-1 min-w-0">
          <span className="font-semibold">A new version of Labor Education is available.</span>{" "}
          <span className="hidden sm:inline opacity-90">Reload to see the latest content and fixes.</span>
        </p>
        <button
          onClick={handleReload}
          data-testid="app-update-reload-btn"
          className="bg-white text-amber-700 hover:bg-amber-50 font-bold text-sm px-4 py-1.5 rounded-md shadow flex-shrink-0 transition-all"
        >
          Reload
        </button>
        <button
          onClick={() => setDismissed(true)}
          data-testid="app-update-dismiss-btn"
          aria-label="Dismiss update banner"
          className="text-white/80 hover:text-white flex-shrink-0 transition-all"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default AppUpdateBanner;
